import axios from '@api';

import {
  getWithCredentials,
  transformDetails,
  transformGlobalFilters,
  transformGlobalFiltersV2,
  transformRawData,
} from './utilities/helpers';
import { getObjectWithoutEmptyValues } from '@utilities';

/**
 * export raw data
 * @param {Object} options
 * @param {Boolean} [options.includeDisqualified] include DISQUALIFIED responses (Calypso only)
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportRawData = async ({ includeDisqualified, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      include_disqualified: includeDisqualified,
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/rawdata/xlsx/',
    withCredentials: true,
  });
};

/**
 * export raw data v2
 * @param {Object} options
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportRawDataV2 = async ({ surveyId }) => {
  return axios({
    method: 'get',
    params: {
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/raw-data-v2/xlsx/',
    withCredentials: true,
  });
};

/**
 * export raw data as SPSS
 * @param {Object} options
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportRawDataSpss = async ({ surveyId }) => {
  return axios({
    method: 'get',
    params: {
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/rawdata/spss/',
    withCredentials: true,
  });
};

/**
 * export summary tab
 * @param {Object} options
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportSummaryData = async ({ surveyId }) => {
  return axios({
    method: 'get',
    params: {
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/summary/xlsx/',
    withCredentials: true,
  });
};

/**
 * export summary tab with v2 endpoint
 * @param {Object} options
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportSummaryDataV2 = async ({ surveyId }) => {
  return axios({
    method: 'get',
    params: {
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/summary-v2/xlsx/',
    withCredentials: true,
  });
};

/**
 * export crosstabs and stattesting xlsx file
 * @param {Object} options
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportStatXtabData = async ({ surveyId }) => {
  return axios({
    method: 'get',
    params: {
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/xtab_stat_sig/xlsx/',
    withCredentials: true,
  });
};

/**
 * export TURF analysis
 * @param {Object} options
 * @param {ExportQuery} [options.query]
 * @return {Promise}
 */
export const exportTurfData = async ({ query }) => {
  return axios({
    method: 'get',
    params: query,
    url: '/v2/api/survey-dashboard/export/turf/xlsx/',
    withCredentials: true,
  });
};

/**
 * export maxdiff preference share file
 * @param {Object} options
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportMaxDiffPrefShareData = async ({ query = {}, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      ...getObjectWithoutEmptyValues(query),
      survey_id: surveyId,
    },
    url: '/v2/api/survey-dashboard/export/preference-share/xlsx/',
    withCredentials: true,
  });
};

/**
 * read details (response counts)
 * @param {Object} options
 * @param {String} [options.publicToken]
 * @param {Number} [options.signal] AbortController signal
 * @param {Number} options.surveyId Insights survey id
 * @return {Promise}
 */
export const readDetails = async ({ publicToken, signal, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      publicToken,
    },
    signal,
    url: `/v2/api/survey-dashboard/details/${surveyId}`,
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => transformDetails(data));
};

/**
 * read global filters
 * @param {Object} options
 * @param {String} options.publicToken
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readGlobalFilters = async ({ publicToken, signal, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      publicToken,
    },
    signal,
    url: `/v2/api/survey-dashboard/global-filters/${surveyId}`,
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => transformGlobalFilters(data));
};

/**
 * read global filters v2
 * @param {Object} options
 * @param {String} options.publicToken
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readGlobalFiltersV2 = async ({ publicToken, signal, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/global-filters-v2/',
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => transformGlobalFiltersV2(data));
};

/**
 * read raw data
 * @param {Object} options
 * @param {Boolean} [options.includeDisqualified] include DISQUALIFIED responses (Calypso only)
 * @param {String} options.publicToken
 * @param {AbortSignal} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readRawData = async ({ includeDisqualified, publicToken, signal, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      include_disqualified: includeDisqualified,
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/raw-data/table',
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => transformRawData(data));
};

/**
 * read raw data v2
 * @param {Object} options
 * @param {String} options.publicToken
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readRawDataV2 = async ({ publicToken, signal, surveyId }) => {
  return axios({
    method: 'get',
    params: {
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/raw-data-v2/table',
    withCredentials: getWithCredentials({ publicToken }),
  }).then(({ data }) => transformRawData(data));
};

export * from './response_quality';
export * from './survey_responses';
